import React, { useMemo } from 'react';
import { Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CommissionRow, PayeeSummaryRow, SalesSummaryRow, QueryResponse } from '../../../services/g306Api';

interface ResultTableProps {
  result: QueryResponse;
}

const fmtNum = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0.00';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const detailColumns: ColumnsType<CommissionRow & { _key: string }> = [
  {
    title: '受款人代號',
    dataIndex: 'fg09_01',
    key: 'fg09_01',
    width: 100,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '出貨單號',
    dataIndex: 'fg09_02',
    key: 'fg09_02',
    width: 80,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '交運日期',
    dataIndex: 'fg09_11',
    key: 'fg09_11',
    width: 100,
    render: (v: string | null) => v || '',
  },
  {
    title: '客戶代號',
    dataIndex: 'fc110_12',
    key: 'fc110_12',
    width: 100,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '業務員',
    dataIndex: 'fc110_13',
    key: 'fc110_13',
    width: 80,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '幣別1',
    dataIndex: 'fg09_15',
    key: 'fg09_15',
    width: 60,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '應付(幣1)',
    dataIndex: 'fg09_12',
    key: 'fg09_12',
    width: 100,
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: '已付(幣1)',
    dataIndex: 'fg09_13',
    key: 'fg09_13',
    width: 100,
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: '餘額(幣1)',
    key: 'balance1',
    width: 100,
    align: 'right',
    render: (_: unknown, record: CommissionRow) => {
      const bal = (record.fg09_12 ?? 0) - (record.fg09_13 ?? 0);
      const color = bal > 0 ? 'blue' : bal < 0 ? 'red' : undefined;
      return <Tag color={color}>{fmtNum(bal)}</Tag>;
    },
  },
  {
    title: '幣別2',
    dataIndex: 'fg09_25',
    key: 'fg09_25',
    width: 60,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '應付(幣2)',
    dataIndex: 'fg09_22',
    key: 'fg09_22',
    width: 100,
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: '已付(幣2)',
    dataIndex: 'fg09_23',
    key: 'fg09_23',
    width: 100,
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: '餘額(幣2)',
    key: 'balance2',
    width: 100,
    align: 'right',
    render: (_: unknown, record: CommissionRow) => {
      const bal = (record.fg09_22 ?? 0) - (record.fg09_23 ?? 0);
      const color = bal > 0 ? 'blue' : bal < 0 ? 'red' : undefined;
      return <Tag color={color}>{fmtNum(bal)}</Tag>;
    },
  },
];

const payeeSummaryColumns: ColumnsType<PayeeSummaryRow & { _key: string }> = [
  { title: '受款人代號', dataIndex: 'payeeCode', key: 'payeeCode', width: 120 },
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
  { title: '應付合計', dataIndex: 'payable', key: 'payable', width: 120, align: 'right', render: (v: number) => fmtNum(v) },
  { title: '已付合計', dataIndex: 'paid', key: 'paid', width: 120, align: 'right', render: (v: number) => fmtNum(v) },
  {
    title: '餘額',
    dataIndex: 'balance',
    key: 'balance',
    width: 120,
    align: 'right',
    render: (v: number) => {
      const color = v > 0 ? 'blue' : v < 0 ? 'red' : undefined;
      return <Tag color={color}>{fmtNum(v)}</Tag>;
    },
  },
];

const salesSummaryColumns: ColumnsType<SalesSummaryRow & { _key: string }> = [
  { title: '業務員代號', dataIndex: 'salesCode', key: 'salesCode', width: 120 },
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
  { title: '應付合計', dataIndex: 'payable', key: 'payable', width: 120, align: 'right', render: (v: number) => fmtNum(v) },
  { title: '已付合計', dataIndex: 'paid', key: 'paid', width: 120, align: 'right', render: (v: number) => fmtNum(v) },
  {
    title: '餘額',
    dataIndex: 'balance',
    key: 'balance',
    width: 120,
    align: 'right',
    render: (v: number) => {
      const color = v > 0 ? 'blue' : v < 0 ? 'red' : undefined;
      return <Tag color={color}>{fmtNum(v)}</Tag>;
    },
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const detailData = useMemo(
    () => (result.details || []).map((row, i) => ({
      ...row,
      _key: `${row.fg09_01}-${row.fg09_02}-${i}`,
    })),
    [result.details]
  );

  const payeeData = useMemo(
    () => (result.payeeSummary || []).map((row, i) => ({
      ...row,
      _key: `${row.payeeCode}-${row.currency}-${i}`,
    })),
    [result.payeeSummary]
  );

  const salesData = useMemo(
    () => (result.salesSummary || []).map((row, i) => ({
      ...row,
      _key: `${row.salesCode}-${row.currency}-${i}`,
    })),
    [result.salesSummary]
  );

  const items = [
    {
      key: 'details',
      label: `佣金明細 (${result.totalCount})`,
      children: (
        <Table
          columns={detailColumns}
          dataSource={detailData}
          rowKey="_key"
          size="small"
          scroll={{ x: 1300, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
  ];

  if (payeeData.length > 0) {
    items.push({
      key: 'payeeSummary',
      label: `受款人別彙總 (${payeeData.length})`,
      children: (
        <Table
          columns={payeeSummaryColumns}
          dataSource={payeeData}
          rowKey="_key"
          size="small"
          scroll={{ x: 600, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    });
  }

  if (salesData.length > 0) {
    items.push({
      key: 'salesSummary',
      label: `業務別彙總 (${salesData.length})`,
      children: (
        <Table
          columns={salesSummaryColumns}
          dataSource={salesData}
          rowKey="_key"
          size="small"
          scroll={{ x: 600, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    });
  }

  return <Tabs items={items} />;
};

export default ResultTable;
